import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Form,
  Pagination,
} from "react-bootstrap";
import {
  FaCalendar,
  FaUser,
  FaSearch,
  FaArrowRight,
  FaEye,
  FaTag,
  FaNewspaper,
  FaClock,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import BlogService from "../../services/blogService";
import "../../styles/blog.css";
import NewsletterSubscribe from "../../components/common/NewsletterSubscribe";

const Blog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [recentPosts, setRecentPosts] = useState([]);

  const categories = [
    { name: "All", value: null, icon: "📚" },
    { name: "Blog", value: "blog", icon: "✍️" },
    { name: "News", value: "news", icon: "📰" },
    { name: "Announcement", value: "announcement", icon: "📢" },
    { name: "Event", value: "event", icon: "🎉" },
  ];

  useEffect(() => {
    loadPosts();
    loadRecentPosts();
    window.scrollTo(0, 0);
  }, [currentPage, selectedCategory]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const response = await BlogService.getAllBlogs(
        currentPage,
        6,
        selectedCategory,
      );
      setPosts(response.data.posts || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error("Failed to load blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentPosts = async () => {
    try {
      const response = await BlogService.getRecentBlogs(5);
      setRecentPosts(response.data || []);
    } catch (error) {
      console.error("Failed to load recent posts:", error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      loadPosts();
      return;
    }

    setLoading(true);
    try {
      const response = await BlogService.searchBlogs(searchTerm);
      setPosts(response.data || []);
      setTotalPages(1);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const getCategoryBadge = (category) => {
    const colors = {
      blog: "primary",
      news: "info",
      announcement: "warning",
      event: "success",
    };
    return colors[category] || "secondary";
  };

  const getCategoryIcon = (category) => {
    const icons = {
      blog: "✍️",
      news: "📰",
      announcement: "📢",
      event: "🎉",
    };
    return icons[category] || "📝";
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = (content || "").split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return readTime > 0 ? readTime : 1;
  };

  const PaginationItems = () => {
    let items = [];
    let maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2)
        items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1)
        items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    return items;
  };

  return (
    <div className="blog-page-wrapper">
      <PublicNavbar />

      {/* Hero Section */}
      <section className="blog-page-hero">
        <Container>
          <div className="blog-page-hero-content text-center">
            <div className="blog-page-hero-badge">
              <FaNewspaper className="me-2" /> Latest Updates
            </div>
            <h1 className="blog-page-hero-title">
              Blog & <span className="blog-page-highlight">News</span>
            </h1>
            <p className="blog-page-hero-subtitle">
              Stay updated with the latest news, announcements, and insights
              from Sensible Academy
            </p>
          </div>
        </Container>
        <div className="blog-page-hero-wave">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
          >
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            ></path>
          </svg>
        </div>
      </section>

      <Container className="blog-page-container py-5">
        <Row>
          {/* Main Content */}
          <Col lg={8}>
            {loading ? (
              <div className="blog-page-loading text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading amazing stories...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="blog-page-no-posts text-center py-5">
                <h4>No posts found</h4>
                <p className="text-muted">Check back later for new content.</p>
              </div>
            ) : (
              <>
                {posts.map((post) => (
                  <Card key={post.id} className="blog-page-card mb-4">
                    <Row className="g-0">
                      {post.featured_image && (
                        <Col md={4}>
                          <div className="blog-page-card-image">
                            <img src={post.featured_image} alt={post.title} />
                            <div className="blog-page-card-category">
                              <span>
                                {getCategoryIcon(post.category)} {post.category}
                              </span>
                            </div>
                          </div>
                        </Col>
                      )}
                      <Col md={post.featured_image ? 8 : 12}>
                        <Card.Body>
                          <div className="blog-page-card-meta">
                            <span>
                              <FaCalendar className="me-1" />{" "}
                              {formatDate(post.published_date)}
                            </span>
                            <span>
                              <FaEye className="me-1" /> {post.views || 0} views
                            </span>
                            <span>
                              <FaUser className="me-1" />{" "}
                              {post.author_name || "Admin"}
                            </span>
                            <span>
                              <FaClock className="me-1" />{" "}
                              {calculateReadTime(post.content)} min read
                            </span>
                          </div>
                          <Card.Title className="blog-page-card-title">
                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                          </Card.Title>
                          <Card.Text className="blog-page-card-text">
                            {post.short_description?.substring(0, 120)}...
                          </Card.Text>
                          <Link
                            to={`/blog/${post.slug}`}
                            className="blog-page-read-more"
                          >
                            Read Article <FaArrowRight className="ms-1" />
                          </Link>
                        </Card.Body>
                      </Col>
                    </Row>
                  </Card>
                ))}

                {totalPages > 1 && (
                  <div className="blog-page-pagination d-flex justify-content-center mt-4">
                    <Pagination>{PaginationItems()}</Pagination>
                  </div>
                )}
              </>
            )}
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            {/* Search Box */}
            <Card className="blog-page-sidebar-card mb-4">
              <Card.Body>
                <h5 className="blog-page-sidebar-title">
                  <FaSearch className="me-2" /> Search Articles
                </h5>
                <Form onSubmit={handleSearch}>
                  <div className="blog-page-search-wrapper">
                    <Form.Control
                      type="text"
                      placeholder="Search posts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="blog-page-search-input"
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      className="blog-page-search-btn"
                    >
                      <FaSearch />
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Categories */}
            <Card className="blog-page-sidebar-card mb-4">
              <Card.Body>
                <h5 className="blog-page-sidebar-title">
                  <FaTag className="me-2" /> Categories
                </h5>
                <div className="blog-page-category-list">
                  {categories.map((cat, index) => (
                    <button
                      key={index}
                      className={`blog-page-category-btn ${selectedCategory === cat.value ? "active" : ""}`}
                      onClick={() => handleCategoryFilter(cat.value)}
                    >
                      <span>{cat.icon}</span> {cat.name}
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>

            {/* Recent Posts */}
            <Card className="blog-page-sidebar-card mb-4">
              <Card.Body>
                <h5 className="blog-page-sidebar-title">
                  <FaClock className="me-2" /> Recent Posts
                </h5>
                {recentPosts.length === 0 ? (
                  <p className="text-muted">No recent posts</p>
                ) : (
                  <div className="blog-page-recent-list">
                    {recentPosts.slice(0, 5).map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="blog-page-recent-item"
                      >
                        {post.featured_image && (
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            className="blog-page-recent-img"
                          />
                        )}
                        <div className="blog-page-recent-content">
                          <h6>{post.title}</h6>
                          <small>{formatDate(post.published_date)}</small>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Newsletter */}

              <NewsletterSubscribe source="blog" variant="default" />

          </Col>
        </Row>
      </Container>

      {/* CTA Section */}
      <section className="blog-page-cta">
        <Container className="text-center">
          <h2 className="blog-page-cta-title">Stay Connected With Us</h2>
          <p className="blog-page-cta-text">
            Follow us for daily updates and educational content
          </p>
          <div className="blog-page-cta-buttons">
            <Button
              variant="light"
              size="lg"
              className="blog-page-cta-btn"
              onClick={() => navigate("/contact")}
            >
              Contact Us
            </Button>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
